import { type VFC, useMemo } from 'react';
import type { SelectProps } from 'antd/lib/select';
import { Select, Skeleton } from 'antd';
import { omit } from 'ramda';
import { type SerializedCustomer, type SerializedFilter, ViewCustomers } from '@enmon/commons/lib/enmon';
import { useCustomers } from 'api/hooks/customer';
import { filterSort, filterOption } from '../utils';

export interface CustomersSelectProps extends SelectProps<string | string[]> {
  filter?: SerializedFilter;
  onChange?: (value: string | string[] | null) => void;
  omittedIds?: SerializedCustomer['_id'][];
}

export const CustomersSelect: VFC<CustomersSelectProps> = props => {
  const { filter, onChange, omittedIds = [], ...propsToPass } = props;

  const { data, isFetching } = useCustomers({
    // Exclude pagination params from filter and set depth to -1 to get children customers
    filter: omit(['page', 'pageSize'], { ...filter, depth: -1 }),
    view: ViewCustomers.Metadata,
  });

  const { result: customersMetadata = [] } = data ?? {};

  const customerOptions = useMemo(
    () =>
      customersMetadata
        .filter(customer => !omittedIds.includes(customer._id))
        .map(({ _id, name }) => ({ value: _id, label: name })),
    [customersMetadata, omittedIds],
  );

  if (isFetching) return <Skeleton.Input active block style={propsToPass.style} />;

  return (
    <Select
      allowClear
      showSearch
      filterOption={filterOption}
      filterSort={filterSort}
      options={customerOptions}
      placeholder="Select customer"
      popupMatchSelectWidth={false}
      style={{ minWidth: 300 }}
      styles={{ popup: { root: { width: 500 } } }}
      onChange={value => onChange?.(value ?? null)}
      {...propsToPass}
    />
  );
};
